import React, { FC, memo, useMemo } from 'react';
import {
  useRequestAllCategoriesQuery,
  useRequestAvailableVehicleCategoriesQuery,
} from '@/features/vehicles/services/Vehicles.service';
import { useFormikContext } from 'formik';
import { BookingOverviewForm } from '@/features/booking/types';
import { Loader, Paragraph, Row, Show } from '@wunder/ui-components';
import VehicleCategoryCard from '@/features/booking/components/BookingFlow/components/VehicleCategorySelection/VehicleCategoryCard';
import { useTypedSelector } from '@/core/redux/hooks';
import { Trans } from '@lingui/react/macro';
import { getActiveFilterValues } from '@/features/account/redux/account.selectors';
import { BookingFlowIDs } from '@/features/booking/components/BookingFlow/BookingFlow';
import { useStationTimezone } from '@/core/features/booking/hooks/useStationTimezone';
import { mergeDateTime } from '@/features/booking/utils/mergeDateTime';
import RequestBookingBanner from '@/features/booking/components/BookingFlow/RequestBookingBanner';
import { useRequestBookableCategoriesQuery } from '@/features/booking/services/Booking.service';
import { MergedAvailableCategoriesData, MergedBookableCategories } from '@/features/vehicles/types';

const VehicleCategorySelection: FC<BookingFlowIDs> = ({ bookingId, branchId }) => {
  const { values, setFieldValue, initialValues } = useFormikContext<BookingOverviewForm>();
  const activeFilters = useTypedSelector(getActiveFilterValues);
  const { convertToStationTimezone } = useStationTimezone(branchId);

  const { data: availableVehicleCategories, isFetching: isFetchingAvailableCategories } =
    useRequestAvailableVehicleCategoriesQuery(
      {
        branchId,
        endBranchId: values.endBranchToggle ? +values.endBranchId! : undefined,
        startTime: convertToStationTimezone(
          mergeDateTime(values.bookingDate!.from!, values.bookingTime?.from),
        )!,
        endTime: convertToStationTimezone(
          mergeDateTime(
            values.bookingDate!.to ?? values.bookingDate?.from!,
            values.bookingTime?.to,
          ),
        ),
        vehicleAttributes: activeFilters?.vehicleAttributes,
        bookingId,
      },
      { skip: values.requestBookingInitiated },
    );

  // In case of booking request, show all categories
  const { data: allCategories, isFetching: isFetchingAllCategories } = useRequestAllCategoriesQuery(
    undefined,
    {
      skip: !values.requestBookingInitiated,
    },
  );

  const { data: bookableCategories, isFetching: isFetchingBookableCategories } =
    useRequestBookableCategoriesQuery(
      {
        branchId,
        startTime: convertToStationTimezone(
          mergeDateTime(values.bookingDate!.from!, values.bookingTime?.from),
        ),
        endTime: convertToStationTimezone(
          mergeDateTime(
            values.bookingDate!.to ?? values.bookingDate?.from!,
            values.bookingTime?.to,
          ),
        ),
      },
      { skip: !values.requestBookingInitiated },
    );

  const vehicleCategories = useMemo(() => {
    if (!(allCategories || bookableCategories) && !availableVehicleCategories) return [];

    if (values.requestBookingInitiated) {
      return bookableCategories
        ?.map((category) => {
          const categoryData = allCategories?.find((cat) => cat.id === category.categoryId);

          return { ...category, ...categoryData };
        })
        ?.filter(Boolean) as MergedBookableCategories[];
    }

    return availableVehicleCategories as MergedAvailableCategoriesData[];
  }, [
    allCategories,
    availableVehicleCategories,
    values.requestBookingInitiated,
    bookableCategories,
  ]);

  if (isFetchingAvailableCategories || isFetchingBookableCategories || isFetchingAllCategories) {
    return <Loader cover />;
  }

  return (
    <div>
      <Paragraph marginBottom={24} weight="medium">
        <Trans id="bookings.createOrUpdate.selecteVehicleCategory">Select vehicle category</Trans>
      </Paragraph>

      <Show
        when={
          !values.requestBookingInitiated && !initialValues.selectedVehicleCategory && !bookingId
        }
      >
        <RequestBookingBanner
          onBookingRequested={() => {
            void setFieldValue('requestBookingInitiated', true);
          }}
        />
      </Show>

      <Row>
        {vehicleCategories?.length ? (
          vehicleCategories?.map((availableCategory) => {
            return (
              <VehicleCategoryCard key={availableCategory.id} categoryData={availableCategory} />
            );
          })
        ) : (
          <Paragraph size={4} textColor="text-400">
            <Trans id="bookings.createOrUpdate.noVehicleCategories">
              There are no vehicle categories available for selected time period. Some of the
              vehicles might only be partially available during this time. Try adjusting your time
              intervals.
            </Trans>
          </Paragraph>
        )}
      </Row>
    </div>
  );
};

export default memo(VehicleCategorySelection);
